import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { BoxProps, Div, Flex } from '../../box';
import Text, { TextProps } from '../../text/text';
import { BlackLink } from '../../link';
import Icon from '../../icon';
import EllipsisWithTooltip from '../../ellipsis-with-tooltip';
import Tag from '../../tag';
import Tooltip from '../../tooltip/tooltip';
import Avatar, { AvatarProps } from '../../avatar/avatar';
import MultiSelector from '../../multi-selector';
import Button from '../../button';
import { MultiSelectorProps } from '../../multi-selector/multi-selector';
import IconLabel, { IconLabelProps } from '../../icon-label/icon-label';
import { getCssRuleWithResponsiveCalculation } from '../../../utils';
import { spacing, tableCellVariants } from './table-cell-constants';

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  padding?: keyof typeof spacing;
  as?: React.ElementType;
  paddingLeft?: keyof typeof spacing;
  paddingRight?: keyof typeof spacing;
  variant?: keyof typeof tableCellVariants;
  verticalAlign?: 'top' | 'middle' | 'bottom';
  colSpan?: number;
  textAlign?: TextProps['align'];
}

const StyledCell = styled.td<TableCellProps>`
  padding: ${props => props.padding && `${spacing[props.padding]} !important`};
  padding-left: ${props => props.paddingLeft && `${spacing[props.paddingLeft]} !important`};
  padding-right: ${props => props.paddingRight && `${spacing[props.paddingRight]} !important`};
  vertical-align: ${props => props.verticalAlign};
  ${props => props.variant && tableCellVariants[props.variant]};
  ${props =>
    getCssRuleWithResponsiveCalculation<TableCellProps['textAlign']>({
      property: 'text-align',
      value: props.textAlign || 'start',
    })};
`;

export const TableCell = (props: TableCellProps) => {
  return <StyledCell {...props} />;
};

export interface TableHeadCellProps extends TableCellProps {
  size?: TextProps['size'];
  textVariant?: TextProps['variant'];
  scope?: any;
  children?: any;
  colSpan?: number;
  width?: string;
  weight?: TextProps['weight'];
}

export const TableHeadCell = (props: TableHeadCellProps) => {
  const { children, weight = 500, size, textVariant = 'inherit', scope, textAlign, ...rest } = props;
  return (
    <TableCell as={'th'} {...rest} scope={scope || (rest.colSpan && rest.colSpan > 1 ? 'colgroup' : 'col')}>
      <Text weight={weight} size={size} align={textAlign} variant={textVariant}>
        {children}
      </Text>
    </TableCell>
  );
};

export interface MultiSelectorTableHeadCellProps extends Pick<MultiSelectorProps, 'value' | 'items' | 'itemToString'> {
  size?: IconLabelProps['size'] & TableHeadCellProps['size'];
  onChange: MultiSelectorProps['onChange'];
  disabled?: MultiSelectorProps['disabled'];
  width?: BoxProps['width'];
  componentClass?: string | React.ElementType;
  colSpan?: number;
  children?: React.ReactNode;
  paddingLeft?: BoxProps['paddingLeft'];
}

export const MultiSelectorTableHeadCell = ({
  size,
  onChange,
  disabled = false,
  value,
  items,
  colSpan,
  width = 'auto',
  itemToString,
  children,
  paddingLeft,
  componentClass = TableHeadCell,
}: MultiSelectorTableHeadCellProps): React.JSX.Element => {
  const Component = componentClass;

  return (
    <Component width={width} size={size} colSpan={colSpan}>
      {items.length ? (
        <Div paddingLeft={paddingLeft}>
          <MultiSelector
            disabled={disabled}
            dropdownSize={'md'}
            onChange={onChange}
            value={value}
            items={items}
            itemToString={itemToString}
          >
            <MultiSelector.Button>
              {({ getToggleButtonProps, isOpen }) => (
                <Button variant={'clean'} size={'auto'} {...getToggleButtonProps({})}>
                  <IconLabel
                    iconVariant={value?.length || isOpen ? 'action' : 'inherit'}
                    variant={'inherit'}
                    size={size}
                    label={children}
                    icon={'FILTER_LIST'}
                  />
                </Button>
              )}
            </MultiSelector.Button>
          </MultiSelector>
        </Div>
      ) : (
        children
      )}
    </Component>
  );
};

export interface SortableTableHeadCellProps extends TableHeadCellProps {
  children?: ReactNode;
  size?: TextProps['size'];
  onRequestSort?: (e: React.MouseEvent<HTMLElement>) => void;
  tooltip?: React.ReactNode;
  active?: boolean;
  isAscending?: boolean;
  componentClass?: string | React.ElementType;
}

export const SortableTableHeadCell = ({
  children,
  size = 'md',
  onRequestSort,
  tooltip,
  isAscending,
  active,
  componentClass = TableHeadCell,
  ...rest
}: SortableTableHeadCellProps) => {
  const Component = componentClass;
  const ariaSort = active ? (isAscending ? 'ascending' : 'descending') : undefined;
  return (
    <Component aria-sort={ariaSort} {...rest}>
      <Tooltip containerComponent={'span'} text={tooltip}>
        <BlackLink size={size} weight={500} decoration={'none'} onClick={onRequestSort}>
          <Flex alignItems={'center'}>
            {children}
            {active && (
              <Flex width={10} marginLeft={'xxs'} alignItems={'center'}>
                <Icon size={'lg'} name={'SWITCH_LEFT'} rotate={isAscending ? 270 : 90} />
              </Flex>
            )}
          </Flex>
        </BlackLink>
      </Tooltip>
    </Component>
  );
};

export interface AvatarTextTableCellProps {
  fullName?: AvatarProps['name'];
  id?: AvatarProps['userId'];
  profileImage?: AvatarProps['photo'];
  avatarSize?: AvatarProps['size'];
  text?: ReactNode | string;
  colSpan?: number;
  to?: string;
  textComponent?: React.ElementType<{ to?: string }>;
  children?: ReactNode;
  width?: any;
  paddingLeft?: keyof typeof spacing;
}

export const AvatarTextTableCell: FC<AvatarTextTableCellProps> = ({
  fullName,
  id,
  profileImage,
  avatarSize = 'xs',
  text,
  colSpan,
  width,
  to,
  textComponent: TextComponent = Text,
  paddingLeft = 'none',
  children,
}) => (
  <TableCell paddingLeft={paddingLeft} colSpan={colSpan} width={width}>
    <Flex alignItems={'center'}>
      <Avatar name={fullName} size={avatarSize} userId={id} photo={profileImage} flexShrink={0} />
      <Flex minWidth={'0'} paddingRight={'sm'} marginLeft={'xs'}>
        <EllipsisWithTooltip tooltipProps={{ block: true, fullWidth: true }}>
          <TextComponent to={to}>{text}</TextComponent>
        </EllipsisWithTooltip>
        {children}
      </Flex>
    </Flex>
  </TableCell>
);

export const LabelWithAvatarTableCell: FC<{
  fullName?: AvatarProps['name'];
  id?: AvatarProps['userId'];
  profileImage?: AvatarProps['photo'];
}> = ({ fullName, id, profileImage }) => (
  <TableCell>
    <Div paddingRight={'sm'}>
      <Tag size={'lg'}>
        <Avatar name={fullName} size={'xs'} userId={id} photo={profileImage} flexShrink={0} />
        <Flex minWidth={'0'} marginLeft={'xs'}>
          <EllipsisWithTooltip tooltipProps={{ block: true, fullWidth: true }}>
            <Text size={'md'} weight={500}>
              {fullName}
            </Text>
          </EllipsisWithTooltip>
        </Flex>
      </Tag>
    </Div>
  </TableCell>
);

export default TableCell;
